import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import Avatar from "../../components/avatar/avatar.component";
import { Button, Card, Chip, EmptyState, Field, Modal, PageSpinner, TextInput } from "../../components/ui";
import { MailIcon, PlusIcon, SearchIcon, UsersIcon } from "../../components/ui/icons";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import { rolePriority } from "../../utilities/constants";

const ROLE_TONE = {
  Owner: "brand",
  Member: "neutral",
  Viewer: "neutral",
  Invited: "warning",
};

const ROLE_NOTE = {
  Owner: "Full access · Can manage users & roles",
  Member: "Can manage schedules · Cannot manage roles",
  Viewer: "View reports & alerts only",
  Invited: "Invitation sent · Not yet accepted",
};

const ASSIGNABLE_ROLES = ["Owner", "Member", "Viewer"];
const INVITE_ROLES = ["Member", "Viewer"];

const Network = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(0);
  const [networkUsers, setNetworkUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isBusy, setIsBusy] = useState(false);

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [inviteUserDetails, setInviteUserDetails] = useState(null);
  const [inviteRole, setInviteRole] = useState("");

  const [isChangeRoleModalOpen, setIsChangeRoleModalOpen] = useState(false);
  const [changeRoleTargetRole, setChangeRoleTargetRole] = useState(null);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);

  const { deviceId } = useParams();

  const getNetworkUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(`/app-api/device/${deviceId}/network`);
      if (data) {
        setNetworkUsers(data.networkUsers || []);
        setCurrentUserRole(data.role);
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data || "Could not load the device network");
    } finally {
      setIsLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    getNetworkUsers();
  }, [getNetworkUsers]);

  const displayedUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return networkUsers;
    return networkUsers.filter((user) => user.displayName?.toLowerCase().includes(query));
  }, [networkUsers, searchQuery]);

  useEffect(() => {
    setSelectedUsers([]);
  }, [displayedUsers]);

  const canManage = currentUserRole >= rolePriority.Owner;
  const isSelected = (user) => selectedUsers.some((s) => s.id === user.id);
  const allSelected = selectedUsers.length > 0 && selectedUsers.length === displayedUsers.length;
  const selectionAllInvited = selectedUsers.length > 0 && selectedUsers.every((u) => u.role === "Invited");
  const selectionAllMembers =
    selectedUsers.length > 0 && selectedUsers.every((u) => u.role === "Member" || u.role === "Viewer");

  const toggleSelectedUser = (user, checked) =>
    setSelectedUsers((prev) => (checked ? [...prev, user] : prev.filter((s) => s.id !== user.id)));

  const toggleSelectAll = (checked) => setSelectedUsers(checked ? displayedUsers : []);

  // --- actions --------------------------------------------------------------
  const confirmChangeUserRole = (targetRole = null, targetUsers = selectedUsers) => {
    setSelectedUsers(targetUsers);
    setChangeRoleTargetRole(targetRole);
    setIsChangeRoleModalOpen(true);
  };

  const handleChangeUserRole = async () => {
    setIsBusy(true);
    try {
      await axios.post(`/app-api/device/${deviceId}/network/modify`, {
        targetUsers: selectedUsers.map((u) => u.id),
        targetRole: changeRoleTargetRole,
      });
      await getNetworkUsers();
      setIsChangeRoleModalOpen(false);
      setChangeRoleTargetRole(null);
      toast.success("User role changed");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data || "Could not change the role");
    } finally {
      setIsBusy(false);
    }
  };

  const confirmRemoveUser = (targetUsers = selectedUsers) => {
    setSelectedUsers(targetUsers);
    setIsRemoveModalOpen(true);
  };

  const handleRemoveUser = async () => {
    setIsBusy(true);
    try {
      await axios.post(`/app-api/device/${deviceId}/network/remove`, {
        targetUsers: selectedUsers.map((u) => u.id),
      });
      await getNetworkUsers();
      setIsRemoveModalOpen(false);
      toast.success("User removed");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data || "Could not remove the user");
    } finally {
      setIsBusy(false);
    }
  };

  const confirmRevokeInvitation = (targetUsers = selectedUsers) => {
    setSelectedUsers(targetUsers);
    setIsRevokeModalOpen(true);
  };

  const handleRevokeInvitation = async () => {
    setIsBusy(true);
    try {
      await axios.post(`/app-api/device/${deviceId}/network/revoke-invite`, {
        targetUsers: selectedUsers.map((u) => u.id),
      });
      await getNetworkUsers();
      setIsRevokeModalOpen(false);
      toast.success("Invitation revoked");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data || "Could not revoke the invitation");
    } finally {
      setIsBusy(false);
    }
  };

  const getInvitedUserDetails = async () => {
    const targetUserEmail = inviteEmail.trim();
    if (!targetUserEmail) {
      setInviteError("Enter an email address.");
      return;
    }
    setInviteError("");
    setIsBusy(true);
    try {
      const { data } = await axios.post(`/app-api/device/${deviceId}/network/check-invite`, {
        targetUserEmail,
      });
      setInviteUserDetails(data);
    } catch (err) {
      console.error(err);
      setInviteError(err.response?.data || "Could not find that user");
    } finally {
      setIsBusy(false);
    }
  };

  const inviteUser = async () => {
    if (!inviteRole) {
      setInviteError("Select a permission role.");
      return;
    }
    setIsBusy(true);
    try {
      await axios.post(`/app-api/device/${deviceId}/network/invite`, {
        targetRole: inviteRole,
        targetUserId: inviteUserDetails.id,
      });
      await getNetworkUsers();
      closeInvite();
      toast.success("User invited!");
    } catch (err) {
      console.error(err);
      setInviteError(err.response?.data || "Could not send the invitation");
    } finally {
      setIsBusy(false);
    }
  };

  const closeInvite = () => {
    setIsInviteModalOpen(false);
    setInviteUserDetails(null);
    setInviteEmail("");
    setInviteRole("");
    setInviteError("");
  };

  if (isLoading && !networkUsers.length) return <PageSpinner />;

  return (
    <>
      <p className="mb-5 text-sm text-gray-500">Everyone who can see or manage this device.</p>

      {error && (
        <Card className="mb-5 border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </Card>
      )}

      <Card className="overflow-visible">
        <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 p-4 nav:p-5">
          <div className="relative min-w-0 flex-1">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search users"
              className="field-input pl-9"
            />
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setIsInviteModalOpen(true)}>
              <PlusIcon className="h-4 w-4" />
              Invite
            </Button>
          )}
        </div>

        {canManage && selectedUsers.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 bg-primary-50 px-4 py-3 nav:px-5">
            <span className="mr-1 text-xs font-semibold text-primary-800">
              {selectedUsers.length} selected
            </span>
            {selectionAllMembers && (
              <>
                <Button variant="secondary" size="sm" onClick={() => confirmChangeUserRole()}>
                  Change role
                </Button>
                <Button variant="secondary" size="sm" onClick={() => confirmRemoveUser()}>
                  Remove
                </Button>
              </>
            )}
            {selectionAllInvited && (
              <Button variant="secondary" size="sm" onClick={() => confirmRevokeInvitation()}>
                Revoke invite
              </Button>
            )}
          </div>
        )}

        {canManage && displayedUsers.length > 0 && (
          <label className="flex items-center gap-3 border-b border-gray-100 px-4 py-2.5 nav:px-5">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => toggleSelectAll(e.target.checked)}
              aria-label="Select all users"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Select all</span>
          </label>
        )}

        {displayedUsers.length === 0 ? (
          <EmptyState
            icon={UsersIcon}
            title={networkUsers.length ? "No users match that search" : "Nobody else has access yet"}
            description={
              networkUsers.length
                ? "Try a different name."
                : "Invite a family member or carer so they can help manage this device."
            }
          />
        ) : (
          <div className="divide-y divide-gray-100">
            {displayedUsers.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                canManage={canManage}
                selected={isSelected(user)}
                onSelect={(checked) => toggleSelectedUser(user, checked)}
                onChangeRole={(role) => confirmChangeUserRole(role, [user])}
                onRemove={() => confirmRemoveUser([user])}
                onRevoke={() => confirmRevokeInvitation([user])}
              />
            ))}
          </div>
        )}
      </Card>

      {/* --- invite --- */}
      <Modal
        isOpen={isInviteModalOpen}
        onRequestClose={closeInvite}
        title="Invite a user"
        footer={
          <>
            {inviteUserDetails && (
              <Button variant="ghost" size="sm" onClick={() => setInviteUserDetails(null)}>
                Back
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={closeInvite}>
              Cancel
            </Button>
            {inviteUserDetails ? (
              <Button onClick={inviteUser} loading={isBusy}>
                Send invite
              </Button>
            ) : (
              <Button onClick={getInvitedUserDetails} loading={isBusy}>
                Proceed
              </Button>
            )}
          </>
        }
      >
        {!inviteUserDetails ? (
          <Field label="Email address" error={inviteError} htmlFor="invite-email">
            <TextInput
              id="invite-email"
              type="email"
              autoFocus
              value={inviteEmail}
              invalid={Boolean(inviteError)}
              placeholder="name@example.com"
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && getInvitedUserDetails()}
            />
          </Field>
        ) : (
          <>
            <div className="mb-5 flex items-center gap-3 rounded-xl bg-gray-50 p-3">
              <Avatar src={inviteUserDetails.photoURL} size="md">
                {inviteUserDetails.displayName}
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-primary-900">{inviteUserDetails.displayName}</p>
                <p className="truncate text-[11px] text-gray-500">{inviteUserDetails.email}</p>
              </div>
            </div>
            <p className="field-label">Permission</p>
            <div className="space-y-2">
              {INVITE_ROLES.map((role) => (
                <RoleOption
                  key={role}
                  name="invite-role"
                  role={role}
                  checked={inviteRole === role}
                  onSelect={() => {
                    setInviteRole(role);
                    setInviteError("");
                  }}
                />
              ))}
            </div>
            {inviteError && <p className="mt-2 text-xs font-medium text-red-600">{inviteError}</p>}
          </>
        )}
      </Modal>

      {/* --- change role --- */}
      <Modal
        isOpen={isChangeRoleModalOpen}
        onRequestClose={() => {
          setChangeRoleTargetRole(null);
          setIsChangeRoleModalOpen(false);
        }}
        title={changeRoleTargetRole === "Owner" ? "Transfer ownership" : "Change permission"}
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setChangeRoleTargetRole(null);
                setIsChangeRoleModalOpen(false);
              }}
            >
              Cancel
            </Button>
            {changeRoleTargetRole && (
              <Button variant="danger" onClick={handleChangeUserRole} loading={isBusy}>
                {changeRoleTargetRole === "Owner" ? "Transfer ownership" : "Change permission"}
              </Button>
            )}
          </>
        }
      >
        {!changeRoleTargetRole ? (
          <>
            <p className="field-label">Select permission</p>
            <div className="space-y-2">
              {INVITE_ROLES.map((role) => (
                <RoleOption
                  key={role}
                  name="target-role"
                  role={role}
                  checked={false}
                  onSelect={() => setChangeRoleTargetRole(role)}
                />
              ))}
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              {changeRoleTargetRole === "Owner"
                ? "Ownership of this device will be transferred to:"
                : `Permissions will be changed to ${changeRoleTargetRole} for:`}
            </p>
            <ul className="mt-3 space-y-1">
              {selectedUsers.map((user) => (
                <li key={user.id} className="text-sm font-semibold text-primary-900">
                  {user.displayName}
                </li>
              ))}
            </ul>
          </>
        )}
      </Modal>

      {/* --- remove --- */}
      <ConfirmModal
        isOpen={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        onConfirm={handleRemoveUser}
        loading={isBusy}
        title="Remove user"
        confirmLabel="Remove"
        message="These users will lose access to this device:"
        users={selectedUsers}
      />

      {/* --- revoke --- */}
      <ConfirmModal
        isOpen={isRevokeModalOpen}
        onClose={() => setIsRevokeModalOpen(false)}
        onConfirm={handleRevokeInvitation}
        loading={isBusy}
        title="Revoke invitation"
        confirmLabel="Revoke invite"
        message="These invitations will be withdrawn:"
        users={selectedUsers}
      />
    </>
  );
};

/** One person. Row from `nav` up, stacked card below it. */
const UserRow = ({ user, canManage, selected, onSelect, onChangeRole, onRemove, onRevoke }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useOnClickOutside(menuRef, () => setMenuOpen(false));

  const actionable = canManage && user.role !== "Owner";

  return (
    <div className="flex items-start gap-3 px-4 py-3.5 nav:items-center nav:px-5">
      {canManage && (
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onSelect(e.target.checked)}
          aria-label={`Select ${user.displayName}`}
          className="mt-1 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500 nav:mt-0"
        />
      )}

      <Avatar src={user.photoURL} size="md">
        {user.displayName}
      </Avatar>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-primary-900">{user.displayName}</p>
        <p className="truncate text-[11px] text-gray-500">{user.email}</p>
        <p className="mt-0.5 text-[11px] text-gray-500">{ROLE_NOTE[user.role]}</p>
        <div className="mt-2 nav:hidden">
          <Chip tone={ROLE_TONE[user.role]}>{user.role}</Chip>
        </div>
      </div>

      <div className="hidden flex-shrink-0 nav:block">
        <Chip tone={ROLE_TONE[user.role]}>{user.role}</Chip>
      </div>

      {actionable && (
        <div className="relative flex-shrink-0" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={`Actions for ${user.displayName}`}
            aria-expanded={menuOpen}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
          >
            <span className="text-lg leading-none">⋯</span>
          </button>

          {menuOpen && (
            <div className="absolute right-0 z-30 mt-1 w-44 overflow-hidden rounded-xl border border-hairline bg-white py-1 shadow-pop">
              {user.role === "Invited" ? (
                <MenuItem
                  onClick={() => {
                    setMenuOpen(false);
                    onRevoke();
                  }}
                >
                  Revoke invite
                </MenuItem>
              ) : (
                <>
                  {ASSIGNABLE_ROLES.map((role) =>
                    user.role === role ? (
                      <p
                        key={role}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-primary-700"
                      >
                        ✓ {role}
                      </p>
                    ) : (
                      <MenuItem
                        key={role}
                        onClick={() => {
                          setMenuOpen(false);
                          onChangeRole(role);
                        }}
                      >
                        {role}
                      </MenuItem>
                    )
                  )}
                  <div className="my-1 border-t border-gray-100" />
                  <MenuItem
                    danger
                    onClick={() => {
                      setMenuOpen(false);
                      onRemove();
                    }}
                  >
                    Remove
                  </MenuItem>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MenuItem = ({ danger, children, ...rest }) => (
  <button
    className={`block w-full px-4 py-2.5 text-left text-sm transition hover:bg-gray-50 ${
      danger ? "text-red-600" : "text-gray-600"
    }`}
    {...rest}
  >
    {children}
  </button>
);

const RoleOption = ({ name, role, checked, onSelect }) => (
  <label
    className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
      checked ? "border-primary-500 bg-primary-50" : "border-gray-200 hover:bg-gray-50"
    }`}
  >
    <input type="radio" name={name} checked={checked} onChange={onSelect} className="mt-0.5" />
    <span>
      <span className="block text-sm font-semibold text-primary-900">{role}</span>
      <span className="block text-[11px] text-gray-500">{ROLE_NOTE[role]}</span>
    </span>
  </label>
);

const ConfirmModal = ({ isOpen, onClose, onConfirm, loading, title, confirmLabel, message, users }) => (
  <Modal
    isOpen={isOpen}
    onRequestClose={onClose}
    title={title}
    size="sm"
    footer={
      <>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onConfirm} loading={loading}>
          {confirmLabel}
        </Button>
      </>
    }
  >
    <p className="text-sm text-gray-600">{message}</p>
    <ul className="mt-3 space-y-1">
      {users.map((user) => (
        <li key={user.id} className="flex items-center gap-2 text-sm font-semibold text-primary-900">
          <MailIcon className="h-3.5 w-3.5 text-gray-400" />
          {user.displayName}
        </li>
      ))}
    </ul>
  </Modal>
);

export default Network;
